import { useCallback, useEffect, useRef, useState } from 'react';

interface PresentModeProps {
  open: boolean;
  slides: string[];
  title?: string;
  onClose: () => void;
}

export default function PresentMode({
  open,
  slides,
  title = 'Presentation',
  onClose,
}: PresentModeProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [chromeVisible, setChromeVisible] = useState(true);
  const hideTimer = useRef<number | null>(null);

  const total = Math.max(1, slides.length);
  const safeIndex = Math.min(index, total - 1);
  const current = slides[safeIndex] || '';

  const go = useCallback(
    (delta: number) => {
      setIndex((i) => {
        const next = i + delta;
        if (next < 0) return 0;
        if (next >= total) return total - 1;
        return next;
      });
    },
    [total]
  );

  const bumpChrome = useCallback(() => {
    setChromeVisible(true);
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setChromeVisible(false), 2200);
  }, []);

  useEffect(() => {
    if (!open) return;
    setIndex(0);
    bumpChrome();

    const el = rootRef.current;
    const req = el?.requestFullscreen?.bind(el);
    if (req) {
      void req().catch(() => {
        /* browser may block; overlay still works */
      });
    }

    return () => {
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
      if (document.fullscreenElement && document.exitFullscreen) {
        void document.exitFullscreen().catch(() => undefined);
      }
    };
  }, [open, bumpChrome]);

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        go(1);
        bumpChrome();
        return;
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        go(-1);
        bumpChrome();
        return;
      }
      if (e.key === 'Home') {
        e.preventDefault();
        setIndex(0);
        bumpChrome();
        return;
      }
      if (e.key === 'End') {
        e.preventDefault();
        setIndex(total - 1);
        bumpChrome();
      }
    };

    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, go, onClose, bumpChrome, total]);

  if (!open) return null;

  return (
    <div
      ref={rootRef}
      className="ps-present"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseMove={bumpChrome}
      onClick={(e) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const x = e.clientX - rect.left;
        if (x > rect.width * 0.66) go(1);
        else if (x < rect.width * 0.33) go(-1);
        bumpChrome();
      }}
    >
      <div className={`ps-present-chrome ${chromeVisible ? 'visible' : ''}`}>
        <div className="ps-present-title">{title}</div>
        <div className="ps-present-meta">
          {safeIndex + 1} / {total}
        </div>
        <button
          type="button"
          className="ps-present-exit"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          Exit (Esc)
        </button>
      </div>

      <div className="ps-present-stage">
        {current ? (
          <img src={current} alt={`Slide ${safeIndex + 1}`} className="ps-present-slide" draggable={false} />
        ) : (
          <div className="ps-present-empty">Nothing to present yet</div>
        )}
      </div>

      <div className={`ps-present-hint ${chromeVisible ? 'visible' : ''}`}>
        ← → navigate · click sides · Esc exit
      </div>
    </div>
  );
}
